import { useState, useEffect, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { regionConfig } from '../services/regionConfig'
import { resolveRegion, type IdentifierType } from '../services/regionResolver'
import { setApiBaseUrl } from '../services/api'
import { RegionProvider, type RegionState } from '../contexts/RegionContext'
import { isCustomDomain } from '../utils/routing'
import { detectCareerRoute, rememberHostWithoutCareerSite } from '../career-site/resolver'
import NotFound from '../pages/NotFound'
import ErrorPage from '../pages/ErrorPage'

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export interface IdentifierInfo {
  identifier: string
  type: IdentifierType
  /** Query the probe sends when the identifier alone does not name the request. */
  params?: Record<string, string>
  /** Probed instead when no region knows this identifier (a host without a career site). */
  fallback?: IdentifierInfo
}

// Static routes that don't need region resolution
const STATIC_ROUTES = ['/404', '/error']

export function extractIdentifier(
  pathname: string,
  customDomain: boolean,
  hostname?: string,
): IdentifierInfo | null {
  const parts = pathname.split('/').filter(Boolean)

  // Static routes — no resolution needed
  if (parts.length === 1 && STATIC_ROUTES.includes(`/${parts[0]}`)) {
    return null
  }

  // Career sites: `/career/<slug>/…` on any host, or a custom domain that may
  // serve one. The preview resolves its region from the editor's URL instead.
  const careerRoute = detectCareerRoute(pathname, hostname || '', customDomain)
  if (careerRoute?.mode === 'preview') {
    return null
  }
  if (careerRoute?.mode === 'path') {
    return { identifier: careerRoute.siteSlug, type: 'career', params: { site: careerRoute.siteSlug, path: careerRoute.sitePath } }
  }
  if (careerRoute?.mode === 'host') {
    return {
      identifier: careerRoute.host,
      type: 'career',
      params: { host: careerRoute.host, path: careerRoute.sitePath },
      fallback: { identifier: careerRoute.host, type: 'hostname' },
    }
  }

  // Candidate links carry their own region in the path and set the API base URL
  // themselves, so there is no client to resolve. Without this the first segment
  // would be read as a client code and sent off to be looked up.
  if (['candidate', 'checklist', 'certifications'].includes(parts[0])) {
    return null
  }

  // /p/:positionUuid or /p/internal/:positionUuid — works on all domains
  if (parts[0] === 'p') {
    const uuid = parts[1] === 'internal' ? parts[2] : parts[1]
    if (uuid) return { identifier: uuid, type: 'positionUuid' }
  }

  // /sites/:apCode — works on all domains
  if (parts[0] === 'sites' && parts[1]) {
    return { identifier: parts[1], type: 'apCode' }
  }

  // /internal/:clientIdentifier or /referral/:clientIdentifier — works on all domains
  if ((parts[0] === 'internal' || parts[0] === 'referral') && parts[1]) {
    const id = parts[1]
    const type: IdentifierType = UUID_RE.test(id) ? 'clientUuid' : 'clientCode'
    return { identifier: id, type }
  }

  // Custom domain: use hostname for all remaining routes
  if (customDomain && hostname) {
    return { identifier: hostname, type: 'hostname' }
  }

  // Path mode: first segment is clientCode
  if (parts[0]) {
    return { identifier: parts[0], type: 'clientCode' }
  }

  return null
}

interface RegionGateProps {
  children: ReactNode
}

export default function RegionGate({ children }: RegionGateProps) {
  const location = useLocation()
  const [state, setState] = useState<'loading' | 'resolved' | 'not-found' | 'error'>('loading')
  const [regionState, setRegionState] = useState<RegionState | null>(null)

  // Skip resolution if multi-region is not configured (backward compat / single-region builds)
  const skipResolution = !regionConfig.isMultiRegion

  // Extract identifier from initial URL only — once resolved, region stays for the session.
  // Navigation within the app doesn't change the region.
  const customDomain = isCustomDomain()
  const info = extractIdentifier(location.pathname, customDomain, window.location.hostname)
  const identifierKey = info ? `${info.type}:${info.identifier}` : null

  useEffect(() => {
    if (skipResolution || !info || !identifierKey) {
      setState('resolved')
      return
    }

    // Already resolved — don't re-resolve on navigation
    if (regionState) {
      return
    }

    let cancelled = false

    const resolve = (target: IdentifierInfo): Promise<{ result: Awaited<ReturnType<typeof resolveRegion>>; info: IdentifierInfo }> =>
      resolveRegion(
        target.identifier,
        target.type,
        regionConfig.getOrder(),
        import.meta.env.VITE_ORBIO_API_KEY,
        target.params,
      )
        .then((result) => ({ result, info: target }))
        .catch((err) => {
          if (target.fallback) {
            // Today's probe decides whenever the career probe does not answer
            // with a site: a host no region knows is remembered, while a 5xx,
            // a throttle or a timeout only means this load skips career mode.
            if (err.message === 'not-found') {
              rememberHostWithoutCareerSite(target.identifier)
            }
            return resolve(target.fallback)
          }
          throw err
        })

    resolve(info)
      .then(({ result, info: resolved }) => {
        if (cancelled) return
        setApiBaseUrl(result.apiUrl)
        setRegionState({
          region: result.region,
          apiUrl: result.apiUrl,
          probeData: result.probeData,
          identifierType: resolved.type,
        })
        setState('resolved')
      })
      .catch((err) => {
        if (cancelled) return
        if (err.message === 'not-found') {
          setState('not-found')
        } else {
          setState('error')
        }
      })

    return () => { cancelled = true }
  }, [identifierKey, skipResolution]) // eslint-disable-line react-hooks/exhaustive-deps

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500" />
      </div>
    )
  }

  if (state === 'not-found') {
    return <NotFound />
  }

  if (state === 'error') {
    return <ErrorPage />
  }

  if (regionState) {
    return <RegionProvider value={regionState}>{children}</RegionProvider>
  }

  // Single-region mode or static route — render without context
  return <>{children}</>
}
